import { color, Icon, space, Tag, typography } from '@primer-io/goat'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import slugify from 'slugify'
import Clickable from 'components/Clickable'
import useWindowHash from 'hooks/useWindowHash'

import { ItemContextProvider, useItemContext } from './ItemContext'
import { Link } from '@primer-io/goat-icons'

/** @param {{ children: React.ReactNode; label?: string; subtitle?: string; title: string }} */
export function Item({ children, label, subtitle, title }) {
	const hash = useWindowHash()
	const { path } = useItemContext()
	const [selected, setSelected] = useState(false)

	const appendedPath = [path, (title && slugify(title)) || (subtitle && slugify(subtitle))].filter(Boolean).join('.')

	// Selection needs to be done client-side
	useEffect(() => {
		setSelected(!!appendedPath && !!hash && appendedPath === hash)
	}, [appendedPath, hash])

	return (
		<Container $selected={selected}>
			<Header as={'div'} id={appendedPath} data-toc='' margin={!!children}>
				<Clickable to={`#${appendedPath}`}>
					<TitleContainer>
						<LinkIcon $selected={selected}>
							<Icon as={Link} size='small' />
						</LinkIcon>

						{title}
					</TitleContainer>
				</Clickable>
				{subtitle && <Small>{subtitle}</Small>}
				{label && <Tag size='small'>{label}</Tag>}
			</Header>
			<ItemContextProvider path={appendedPath}>{children}</ItemContextProvider>
		</Container>
	)
}

const LinkIcon = styled.div`
	color: ${p => (p.$selected ? color('gray-900') : color('gray-500'))};
	transition: color 200ms linear;
	position: relative;
	top: 3px;
`

const Container = styled.div`
	${typography('body-large')};
	color: ${color('gray-900')};
	display: flex;
	flex-direction: column;
	gap: ${space(4)};
	padding: ${space(4)};

	> :not(h3:first-child, ul) {
		margin: 0;
	}

	outline-style: solid;
	outline-color: hsla(240, 100%, 73%, 0);
	outline-width: 2px;
	border-radius: 0px;
	transition:
		200ms ease outline-color,
		200ms ease border-radius;

	${p =>
		p.$selected &&
		`
		outline-color: hsla(240, 100%, 73%, 1);
		border-radius: 2px;
		`}
`

const Header = styled.h3`
	display: inline-flex;

	margin: 0;
	margin-bottom: ${p => (p.margin ? space(-2) : 0)};
	place-items: baseline;
	flex-wrap: wrap;

	gap: ${space(1)};
`

const TitleContainer = styled.div`
	${typography('code')};

	display: inline-flex;
	font-size: 13px;
	font-weight: 500;
	line-height: 20px;

	place-items: baseline;

	&:hover {
		& ${LinkIcon} {
			color: ${color('gray-800')};
		}
	}
`

const Small = styled.small`
	${typography('body-small')};
	color: ${color('gray-600')};
`
